import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Settings as SettingsIcon, Check } from 'lucide-react'
import {
  useCalendarLabelPrefs,
  type CalendarLabelKey,
} from '@/lib/useCalendarLabelPrefs'

// Gear-icon trigger + portaled popover with the 7 label toggles.
// Mirrors NotificationBell's portal positioning (escapes any
// overflow:hidden ancestors). Click-outside / Esc / resize close.

type Group = { label: string; items: { key: CalendarLabelKey; label: string }[] }

const GROUPS: Group[] = [
  { label: 'Who', items: [
    { key: 'customer', label: 'Customer' },
    { key: 'assignee', label: 'Assigned tech' },
  ] },
  { label: 'What', items: [
    { key: 'title', label: 'Job title' },
    { key: 'service_type', label: 'Service type' },
  ] },
  { label: 'When / Where', items: [
    { key: 'time', label: 'Time' },
    { key: 'city_state', label: 'City + State' },
  ] },
  { label: 'Status', items: [
    { key: 'status_text', label: 'Status (text)' },
  ] },
]

const PANEL_WIDTH = 260

export default function CalendarLabelToggles() {
  const { prefs, setPref } = useCalendarLabelPrefs()
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)
  const [open, setOpen] = useState(false)
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null)

  const openPanel = () => {
    const rect = triggerRef.current?.getBoundingClientRect()
    if (!rect) return
    // Anchor right edge of panel to right edge of trigger; drop below.
    const left = Math.max(8, rect.right - PANEL_WIDTH)
    setCoords({ top: rect.bottom + 6, left })
    setOpen(true)
  }

  const close = () => setOpen(false)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (panelRef.current?.contains(t)) return
      if (triggerRef.current?.contains(t)) return
      close()
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close() }
    const onResize = () => close()
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    window.addEventListener('resize', onResize)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('resize', onResize)
    }
  }, [open])

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => (open ? close() : openPanel())}
        aria-label="Calendar label settings"
        title="Calendar label settings"
        style={{
          width: 28, height: 28, borderRadius: 8,
          background: open ? 'var(--accent-dim)' : 'var(--field-bg)',
          border: '1px solid var(--border-default)',
          cursor: 'pointer',
          color: open ? 'var(--accent-text)' : 'var(--ink-secondary)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <SettingsIcon style={{ width: 14, height: 14 }} />
      </button>

      {open && coords && createPortal(
        <div
          ref={panelRef}
          role="dialog"
          aria-label="Calendar label toggles"
          style={{
            position: 'fixed',
            top: coords.top,
            left: coords.left,
            width: PANEL_WIDTH,
            maxHeight: '70vh',
            background: 'var(--surface)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 12,
            boxShadow: 'var(--popup-shadow)',
            zIndex: 100,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <div style={{ padding: '12px 14px 8px', borderBottom: '1px solid var(--border-subtle)' }}>
            <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: 'var(--ink-primary)' }}>
              Calendar labels
            </p>
            <p style={{ margin: '3px 0 0', fontSize: 10, color: 'var(--ink-tertiary)' }}>
              Color always shows status.
            </p>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '6px 0' }}>
            {GROUPS.map(group => (
              <div key={group.label} style={{ padding: '8px 14px 4px' }}>
                <p style={{ margin: '0 0 4px', fontSize: 10, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {group.label}
                </p>
                {group.items.map(item => {
                  const enabled = prefs[item.key]
                  return (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => setPref(item.key, !enabled)}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '7px 4px',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        textAlign: 'left',
                        borderRadius: 6,
                        transition: 'background 100ms',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'var(--hover-bg)' }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'none' }}
                    >
                      <span
                        aria-hidden
                        style={{
                          width: 16, height: 16, borderRadius: 4,
                          border: `1.5px solid ${enabled ? 'var(--accent)' : 'var(--border-default)'}`,
                          background: enabled ? 'var(--accent)' : 'transparent',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          flexShrink: 0,
                          transition: 'all 120ms',
                        }}
                      >
                        {enabled && <Check style={{ width: 11, height: 11, color: 'var(--accent-ink)' }} />}
                      </span>
                      <span style={{ flex: 1, fontSize: 13, color: 'var(--ink-primary)' }}>
                        {item.label}
                      </span>
                    </button>
                  )
                })}
              </div>
            ))}
          </div>
        </div>,
        document.body,
      )}
    </>
  )
}
